import { useEffect, useRef, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Eye, EyeOff, Music, Music2 } from 'lucide-react';
import { useEditorStore } from '../../stores/editorStore';
import { useProjectStore } from '../../stores/projectStore';
import { useMusicStore } from '../../stores/musicStore';
import { Button } from '../atoms/Button';
import type { SceneWithShots } from '../../types/database';

interface VideoPlayerProps {
  scenes: SceneWithShots[];
}

export function VideoPlayer({ scenes }: VideoPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineSecRef = useRef(0);

  const {
    currentTrack,
    volume: musicVolume,
    isMuted: isMusicMuted,
    toggleMute: toggleMusicMute,
  } = useMusicStore();
  
  const {
    isPlaying,
    progress,
    autoNarrate,
    isNarrating,
    showNarration,
    currentSceneIdx,
    currentShotIdx,
    manualSceneIdx,
    manualShotIdx,
    setIsPlaying,
    setProgress,
    setIsNarrating,
    setAutoNarrate,
    setShowNarration,
    setManualSceneIdx,
    setManualShotIdx,
    goToNextShot,
    goToPrevShot,
    goToShot,
  } = useEditorStore();

  // Determine active indices based on play state
  const activeSceneIdx = isPlaying ? currentSceneIdx : manualSceneIdx;
  const activeShotIdx = isPlaying ? currentShotIdx : manualShotIdx;
  
  const currentScene = scenes[activeSceneIdx] || scenes[0];
  const currentShot = currentScene?.shots?.[activeShotIdx] || currentScene?.shots?.[0];

  // Helper: compute elapsed seconds up to (but not including) a given scene/shot
  const getElapsedBefore = useCallback((sceneIdx: number, shotIdx: number) => {
    let elapsed = 0;
    for (let si = 0; si < scenes.length; si++) {
      const scene = scenes[si];
      for (let shi = 0; shi < (scene.shots?.length || 0); shi++) {
        if (si === sceneIdx && shi === shotIdx) return elapsed;
        elapsed += Number(scene.shots[shi]?.duration_sec) || 5;
      }
    }
    return elapsed;
  }, [scenes]);

  // Update timelineSecRef when shot changes (manual navigation or playback shot change)
  useEffect(() => {
    timelineSecRef.current = getElapsedBefore(activeSceneIdx, activeShotIdx);
  }, [activeSceneIdx, activeShotIdx, getElapsedBefore]);

  // Background music: init audio element and keep src/volume in sync
  useEffect(() => {
    if (!currentTrack?.audio_url) return;

    if (!bgMusicRef.current) {
      bgMusicRef.current = new Audio();
      bgMusicRef.current.loop = true;
    }

    const bgAudio = bgMusicRef.current;

    if (bgAudio.src !== currentTrack.audio_url) {
      bgAudio.src = currentTrack.audio_url;
    }

    bgAudio.volume = isMusicMuted ? 0 : musicVolume;
  }, [currentTrack, musicVolume, isMusicMuted]);

  // Background music: play/pause sync — seek to timeline position on play
  useEffect(() => {
    const bgAudio = bgMusicRef.current;
    if (!bgAudio || !currentTrack?.audio_url) return;

    if (isPlaying && !isMusicMuted) {
      // Seek to the current timeline position
      const seekTo = timelineSecRef.current;
      if (bgAudio.duration && isFinite(bgAudio.duration)) {
        bgAudio.currentTime = seekTo % bgAudio.duration;
      }
      bgAudio.play().catch((err) => console.warn('[VideoPlayer] BG music play failed:', err));
    } else {
      bgAudio.pause();
    }
  }, [isPlaying, isMusicMuted, currentTrack]);

  // Player engine - progress tracking
  useEffect(() => {
    if (isPlaying && currentShot) {
      const duration = (Number(currentShot.duration_sec) || 5) * 1000;
      const step = 50; // Update every 50ms
      
      // Reset timeline ref to shot start so music stays in sync with video
      timelineSecRef.current = getElapsedBefore(currentSceneIdx, currentShotIdx);
      
      let currentProgress = 0;
      progressIntervalRef.current = setInterval(() => {
        currentProgress += (step / duration) * 100;
        timelineSecRef.current += step / 1000; // advance timeline by 50ms
        if (currentProgress >= 100) {
          currentProgress = 0;
          goToNextShot(scenes);
        }
        setProgress(currentProgress);
      }, step);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, currentSceneIdx, currentShotIdx, scenes, currentShot, setProgress, goToNextShot]);

  // Auto-narration effect
  useEffect(() => {
    if (isPlaying && autoNarrate && currentShot?.narration_text) {
      // Start narration when play begins or when shot changes
      if (progress === 0 || !isNarrating) {
        speakText(currentShot.narration_text);
      }
    }
  }, [isPlaying, currentShot, autoNarrate, progress, isNarrating]);

  // Reset progress when shot changes manually
  useEffect(() => {
    setProgress(0);
    // Reset video position to beginning when shot changes
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, [activeSceneIdx, activeShotIdx, setProgress]);

  const speakText = useCallback(async (text: string) => {
    if (!text) return;
    
    // Stop any ongoing speech before starting new one
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    
    setIsNarrating(true);
    
    try {
      // This would call the Edge Function in production
      // For now, we'll use the Web Speech API as a fallback
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'hr-HR';
        utterance.rate = 0.9;
        utterance.onend = () => setIsNarrating(false);
        speechSynthesis.speak(utterance);
      }
    } catch (err) {
      console.error('TTS Error:', err);
      setIsNarrating(false);
    }
  }, [setIsNarrating]);

  const handlePlayPause = () => {
    if (isPlaying) {
      // When pausing, sync manual indices with current indices
      setManualSceneIdx(currentSceneIdx);
      setManualShotIdx(currentShotIdx);
      // Stop speech synthesis immediately
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      setIsNarrating(false);
    }
    setIsPlaying(!isPlaying);
  };

  const handlePrevShot = () => {
    if (manualShotIdx > 0) {
      goToShot(manualSceneIdx, manualShotIdx - 1);
    } else if (manualSceneIdx > 0) {
      const prevScene = scenes[manualSceneIdx - 1];
      const lastShotIdx = (prevScene?.shots?.length || 1) - 1;
      goToShot(manualSceneIdx - 1, lastShotIdx);
    }
  };

  const handleNextShot = () => {
    const currentSceneShots = scenes[manualSceneIdx]?.shots?.length || 0;
    
    if (manualShotIdx < currentSceneShots - 1) {
      goToShot(manualSceneIdx, manualShotIdx + 1);
    } else if (manualSceneIdx < scenes.length - 1) {
      goToShot(manualSceneIdx + 1, 0);
    }
  };

  const renderMedia = () => {
    if (!currentShot) {
      return (
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center text-slate-700 font-bold text-xl">
          NO SIGNAL
        </div>
      );
    }

    console.log('[VideoPlayer] Rendering media:', {
      visual_type: currentShot.visual_type,
      media_url: currentShot.media_url,
      thumbnail_url: currentShot.thumbnail_url,
    });

    if (currentShot.visual_type === 'video' && currentShot.media_url) {
      return (
        <video
          ref={videoRef}
          key={`${currentShot.id}-${currentShot.media_url}`}
          src={currentShot.media_url}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          onError={(e) => {
            console.error('[VideoPlayer] Video load error:', e);
          }}
          onLoadStart={() => {
            console.log('[VideoPlayer] Video load started:', currentShot.media_url);
          }}
          onLoadedData={() => {
            console.log('[VideoPlayer] Video loaded successfully');
          }}
        />
      );
    }

    return (
      <img
        key={`${currentShot.id}-${currentShot.thumbnail_url || currentShot.media_url}`}
        src={currentShot.thumbnail_url || currentShot.media_url || ''}
        alt={currentShot.name}
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          console.error('[VideoPlayer] Image load error:', e);
        }}
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative aspect-video rounded-[2rem] overflow-hidden border-2 border-white/10 shadow-2xl bg-black group/video">
        {/* Media Content */}
        {renderMedia()}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

        {/* Text Overlay */}
        {currentShot?.text_enabled && currentShot?.text && (
          <div className="absolute top-6 left-0 right-0 p-4 flex justify-center pointer-events-none z-10">
            <div className="bg-black/60 backdrop-blur-md p-3 rounded-2xl max-w-2xl text-center border border-white/10 shadow-2xl">
              <p className="text-base md:text-lg font-bold text-white drop-shadow-md leading-relaxed">
                {currentShot.text}
              </p>
            </div>
          </div>
        )}

        {/* Subtitles / Narration Overlay */}
        {currentShot?.narration_text && showNarration && (
          <div className="absolute bottom-6 left-0 right-0 p-4 flex justify-center pointer-events-none z-10">
            <div className="bg-black/60 backdrop-blur-md p-3 rounded-2xl max-w-2xl text-center border border-white/10 shadow-2xl">
              <p className="text-base md:text-lg font-bold text-white drop-shadow-md italic leading-relaxed">
                {currentShot.narration_text}
              </p>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {isPlaying && (
          <div className="absolute bottom-0 left-0 w-full h-1.5 bg-white/10 z-30">
            <div
              className="h-full bg-blue-500 transition-all duration-75 shadow-[0_0_15px_rgba(59,130,246,1)]"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Play/Pause Controls - Outside Video */}
      <div className="flex justify-center items-center gap-8">
        {/* Left: Shot Info */}
        <div className="flex items-center gap-2">
          <span className="bg-blue-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase text-white shadow-lg">
            Kadar {activeSceneIdx + 1}.{activeShotIdx + 1}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setAutoNarrate(!autoNarrate)}
            className="p-1.5 bg-black/40 text-white hover:bg-blue-600 border border-white/10"
          >
            {autoNarrate ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowNarration(!showNarration)}
            className={`p-1.5 text-white hover:bg-blue-600 border border-white/10 ${
              showNarration ? 'bg-black/40' : 'bg-black/20 opacity-50'
            }`}
          >
            {showNarration ? <Eye size={14} /> : <EyeOff size={14} />}
          </Button>
        </div>

        {/* Center: Player Controls */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevShot}
            className="p-2 bg-white/10 hover:bg-blue-600 backdrop-blur-md border border-white/10 rounded-full"
          >
            <SkipBack size={18} />
          </Button>
          
          <Button
            variant="primary"
            onClick={handlePlayPause}
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl scale-100 hover:scale-110 transition-transform"
          >
            {isPlaying ? (
              <Pause size={24} />
            ) : (
              <Play size={24} className="ml-1" />
            )}
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextShot}
            className="p-2 bg-white/10 hover:bg-blue-600 backdrop-blur-md border border-white/10 rounded-full"
          >
            <SkipForward size={18} />
          </Button>
        </div>

        {/* Right: Music toggle */}
        <div className="w-32 flex items-center justify-end gap-2">
          {currentTrack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMusicMute}
              className={`p-1.5 text-white hover:bg-purple-600 border border-white/10 ${
                !isMusicMuted ? 'bg-purple-600/30' : 'bg-black/20 opacity-50'
              }`}
              title={isMusicMuted ? 'Omogući muziku' : 'Isključi muziku'}
            >
              {isMusicMuted ? <Music2 size={14} /> : <Music size={14} />}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
